import { CommonModule } from '@angular/common'
import {
  Component,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  inject,
} from '@angular/core'
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { MatTableDataSource, MatTableModule } from '@angular/material/table'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatSelectModule } from '@angular/material/select'
import { MatIconModule } from '@angular/material/icon'
import { MatButtonModule } from '@angular/material/button'
import { TranslationService } from '../../../../../services/translation.service'
import { FprPlasticRow, FprMethodUsed } from '../../../../../types/reports'
import { Subject, debounceTime, takeUntil } from 'rxjs'
import {
  CALCULATION_METHOD_SPECIFIC_COMPONENT_IDENTIFICATION,
  CALCULATION_METHOD_AVERAGE_BILL_OF_MATERIALS,
  CALCULATION_METHOD_FIXED_FACTOR,
} from '../../../../../constants/reports'
import { MatBottomSheet } from '@angular/material/bottom-sheet'
import { PlasticResinsSheetComponent } from './plastic-resins-sheet/plastic-resins-sheet.component'
import { ReportService } from '../../../../../services/report.service'
import { MatTooltipModule } from '@angular/material/tooltip'

@Component({
  selector: 'app-plastics',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatTableModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
  ],
  templateUrl: './plastics.component.html',
  styleUrls: ['./plastics.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlasticsComponent implements OnChanges, OnDestroy {
  private fb = inject(FormBuilder)
  translationService = inject(TranslationService)
  private bottomSheet = inject(MatBottomSheet)
  private reportService = inject(ReportService)

  // Data flows in from the parent; this component owns the form + table
  @Input() rows: FprPlasticRow[] = []
  @Input() isEditMode = false

  // Emit minimal patches that the parent can persist (keeps child transport-agnostic)
  @Output() patches = new EventEmitter<
    Array<{ id: string; fields: Partial<FprPlasticRow> }>
  >()
  // Fire when user clicks Generate in the empty-state (parent handles generation).
  @Output() generate = new EventEmitter<void>()
  // Emit when any row enters or leaves an editing state so the parent can pause polling.
  @Output() editing = new EventEmitter<boolean>()

  private isEditing = false

  dataSource = new MatTableDataSource<FprPlasticRow>()
  rootForm: FormGroup
  get form(): FormArray<FormGroup> {
    return this.rootForm.get('rows') as FormArray<FormGroup>
  }

  // Lightweight cache so we can compute minimal diffs on valueChanges
  private lastEmittedSnapshot = new Map<string, string>()

  //Grouping/highlight caches (for totals → children visuals)
  private totalsChildren = new Map<string, Set<string>>() // totalsId -> child ids
  private childToTotals = new Map<string, string>() // childId -> totalsId
  private firstChildIds = new Set<string>()
  private lastChildIds = new Set<string>()

  // Fast lookup: row id : row FormGroup
  private idToForm = new Map<string, FormGroup>()

  // All quantity controls we may enable/disable together.
  private readonly QUANTITY_KEYS = [
    'qtyManufactured',
    'qtyImported',
    'qtyPlacedOnMarket',
    'qtyAlberta',
    'qtyBritishColumbia',
    'qtyManitoba',
    'qtyNewBrunswick',
    'qtyNewfoundlandAndLabrador',
    'qtyNovaScotia',
    'qtyOntario',
    'qtyPrinceEdwardIsland',
    'qtyQuebec',
    'qtySaskatchewan',
    'qtyNorthwestTerritories',
    'qtyNunavut',
    'qtyYukon',
    'qtyWasteGeneratedAtPremises',
    'qtyCollectedAtEndOfLife',
    'qtyDivertedDirectReuse',
    'qtyDivertedRefurbishment',
    'qtyDivertedRepair',
    'qtyDivertedRemanufacturing',
    'qtyDivertedMechanicalRecycling',
    'qtyDivertedChemicalRecycling',
    'qtyDivertedProcessingIntoChemicalsFuels',
    'qtyDivertedComposting',
    'qtyDivertedIncinerationWithEnergyRecovery',
    'qtyDivertedIncinerationIndustrialProcesses',
    'qtyFinalDisposalIncinerationNoEnergy',
    'qtyFinalDisposalLandfill',
  ] as const

  // Keep subscriptions tidy.
  private readonly destroyed$ = new Subject<void>()

  readonly displayedColumns: string[] = [
    'productIdentifier',
    'sku',
    'productName',
    'supplier',
    'category',
    'subCategory',
    'resins',
    'reportingDate',
    'methodUsed',
    'qtyManufactured',
    'qtyImported',
    'qtyPlacedOnMarket',
    'qtyAlberta',
    'qtyBritishColumbia',
    'qtyManitoba',
    'qtyNewBrunswick',
    'qtyNewfoundlandAndLabrador',
    'qtyNovaScotia',
    'qtyOntario',
    'qtyPrinceEdwardIsland',
    'qtyQuebec',
    'qtySaskatchewan',
    'qtyNorthwestTerritories',
    'qtyNunavut',
    'qtyYukon',
    'qtyWasteGeneratedAtPremises',
    'qtyCollectedAtEndOfLife',
    'qtyDivertedDirectReuse',
    'qtyDivertedRefurbishment',
    'qtyDivertedRepair',
    'qtyDivertedRemanufacturing',
    'qtyDivertedMechanicalRecycling',
    'qtyDivertedChemicalRecycling',
    'qtyDivertedProcessingIntoChemicalsFuels',
    'qtyDivertedComposting',
    'qtyDivertedIncinerationWithEnergyRecovery',
    'qtyDivertedIncinerationIndustrialProcesses',
    'qtyFinalDisposalIncinerationNoEnergy',
    'qtyFinalDisposalLandfill',
  ]

  constructor() {
    this.rootForm = this.fb.group({ rows: this.fb.array<FormGroup>([]) })

    // Emit minimal patches on debounced form changes; parent decides when/how to persist.
    this.rootForm.valueChanges
      .pipe(debounceTime(800), takeUntil(this.destroyed$))
      .subscribe(() => {
        this.emitPatches()
        this.updateEditingState()
      })
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['rows']) {
      const next: FprPlasticRow[] = changes['rows'].currentValue || []
      if (this.form.length === 0) {
        this.setRows(next)
      } else {
        this.applyServerRows(next)
      }
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next()
    this.destroyed$.complete()
  }

  // UI helpers used by the template
  // True if the row belongs to any totals group.
  isGroupChild(index: number): boolean {
    const id = this.dataSource.data[index]?.id
    return !!id && this.childToTotals.has(id)
  }
  // True if the row is the first child in its contiguous group.
  isGroupChildStart(index: number): boolean {
    const id = this.dataSource.data[index]?.id
    return !!id && this.firstChildIds.has(id)
  }
  // True if the row is the last child in its contiguous group.
  isGroupChildEnd(index: number): boolean {
    const id = this.dataSource.data[index]?.id
    return !!id && this.lastChildIds.has(id)
  }

  getMethodOptions(r: FprPlasticRow): FprMethodUsed[] {
    // Totals row: if all children use Fixed - lock to Fixed, else allow ABOM or Specific.
    if (r.isTotalRow) {
      const childIds = this.totalsChildren.get(r.id)
      if (childIds && childIds.size > 0) {
        const children = this.dataSource.data.filter((x) => childIds.has(x.id))
        const allFixed =
          children.length > 0 &&
          children.every(
            (x) =>
              (x.methodUsed as FprMethodUsed) ===
              (CALCULATION_METHOD_FIXED_FACTOR as FprMethodUsed),
          )
        return allFixed
          ? [CALCULATION_METHOD_FIXED_FACTOR as FprMethodUsed]
          : [
              CALCULATION_METHOD_AVERAGE_BILL_OF_MATERIALS as FprMethodUsed,
              CALCULATION_METHOD_SPECIFIC_COMPONENT_IDENTIFICATION as FprMethodUsed,
            ]
      }
      return [
        CALCULATION_METHOD_AVERAGE_BILL_OF_MATERIALS as FprMethodUsed,
        CALCULATION_METHOD_SPECIFIC_COMPONENT_IDENTIFICATION as FprMethodUsed,
      ]
    }

    // Child rows: methodology is locked to whatever the product has.
    return [r.methodUsed as FprMethodUsed]
  }

  private setRows(rows: FprPlasticRow[]): void {
    // Shallow assign and normalize per-row method before building controls.
    this.dataSource.data = rows.map((r) => ({ ...r }))
    this.dataSource.data.forEach((r) => this.normalizeMethodForRow(r))

    // Build the form fresh
    const fa = this.form
    while (fa.length) fa.removeAt(0)
    this.idToForm.clear()
    this.dataSource.data.forEach((r) => {
      const fg = this.buildRowForm(r)
      fa.push(fg)
      this.idToForm.set(r.id, fg)

      if (r.isTotalRow) {
        fg.get('methodUsed')
          ?.valueChanges.pipe(takeUntil(this.destroyed$))
          .subscribe(() => this.applyTotalsQuantityRulesForTotal(r.id))
      }
    })

    // Seed snapshots used for minimal patching
    this.lastEmittedSnapshot.clear()
    for (const r of this.dataSource.data) {
      this.lastEmittedSnapshot.set(
        r.id,
        JSON.stringify(this.editableSubsetFromRow(r)),
      )
    }

    this.recomputeChildCaches()
    this.applyTotalsQuantityRulesForAllTotals()
    this.updateEditingState()
  }

  private applyServerRows(serverRows: FprPlasticRow[]): void {
    if (!serverRows || serverRows.length === 0) return
    this.setRows(serverRows)
  }

  private buildRowForm(r: FprPlasticRow): FormGroup {
    this.normalizeMethodForRow(r)
    const eolLocked = this.getEolBreakdownLocked(r) || !!r.qtyLocked
    const core = (v: number) => ({ value: v, disabled: !!r.qtyLocked })
    const eol = (v: number) => ({ value: v, disabled: eolLocked })

    // Important: set disabled state at creation time to avoid change-after-check warnings.
    return this.fb.group({
      id: [r.id],
      methodUsed: [
        { value: r.methodUsed, disabled: !r.isTotalRow || !!r.methodLocked },
      ],
      qtyManufactured: [core(r.qtyManufactured), [Validators.min(0)]],
      qtyImported: [core(r.qtyImported), [Validators.min(0)]],
      qtyPlacedOnMarket: [core(r.qtyPlacedOnMarket), [Validators.min(0)]],
      qtyAlberta: [core(r.qtyAlberta), [Validators.min(0)]],
      qtyBritishColumbia: [core(r.qtyBritishColumbia), [Validators.min(0)]],
      qtyManitoba: [core(r.qtyManitoba), [Validators.min(0)]],
      qtyNewBrunswick: [core(r.qtyNewBrunswick), [Validators.min(0)]],
      qtyNewfoundlandAndLabrador: [
        core(r.qtyNewfoundlandAndLabrador),
        [Validators.min(0)],
      ],
      qtyNovaScotia: [core(r.qtyNovaScotia), [Validators.min(0)]],
      qtyOntario: [core(r.qtyOntario), [Validators.min(0)]],
      qtyPrinceEdwardIsland: [
        core(r.qtyPrinceEdwardIsland),
        [Validators.min(0)],
      ],
      qtyQuebec: [core(r.qtyQuebec), [Validators.min(0)]],
      qtySaskatchewan: [core(r.qtySaskatchewan), [Validators.min(0)]],
      qtyNorthwestTerritories: [
        core(r.qtyNorthwestTerritories),
        [Validators.min(0)],
      ],
      qtyNunavut: [core(r.qtyNunavut), [Validators.min(0)]],
      qtyYukon: [core(r.qtyYukon), [Validators.min(0)]],
      qtyWasteGeneratedAtPremises: [
        core(r.qtyWasteGeneratedAtPremises),
        [Validators.min(0)],
      ],
      qtyCollectedAtEndOfLife: [
        core(r.qtyCollectedAtEndOfLife),
        [Validators.min(0)],
      ],
      qtyDivertedDirectReuse: [
        eol(r.qtyDivertedDirectReuse),
        [Validators.min(0)],
      ],
      qtyDivertedRefurbishment: [
        eol(r.qtyDivertedRefurbishment),
        [Validators.min(0)],
      ],
      qtyDivertedRepair: [eol(r.qtyDivertedRepair), [Validators.min(0)]],
      qtyDivertedRemanufacturing: [
        eol(r.qtyDivertedRemanufacturing),
        [Validators.min(0)],
      ],
      qtyDivertedMechanicalRecycling: [
        eol(r.qtyDivertedMechanicalRecycling),
        [Validators.min(0)],
      ],
      qtyDivertedChemicalRecycling: [
        eol(r.qtyDivertedChemicalRecycling),
        [Validators.min(0)],
      ],
      qtyDivertedProcessingIntoChemicalsFuels: [
        eol(r.qtyDivertedProcessingIntoChemicalsFuels),
        [Validators.min(0)],
      ],
      qtyDivertedComposting: [
        eol(r.qtyDivertedComposting),
        [Validators.min(0)],
      ],
      qtyDivertedIncinerationWithEnergyRecovery: [
        eol(r.qtyDivertedIncinerationWithEnergyRecovery),
        [Validators.min(0)],
      ],
      qtyDivertedIncinerationIndustrialProcesses: [
        eol(r.qtyDivertedIncinerationIndustrialProcesses),
        [Validators.min(0)],
      ],
      qtyFinalDisposalIncinerationNoEnergy: [
        eol(r.qtyFinalDisposalIncinerationNoEnergy),
        [Validators.min(0)],
      ],
      qtyFinalDisposalLandfill: [
        eol(r.qtyFinalDisposalLandfill),
        [Validators.min(0)],
      ],
    })
  }

  private updateEditingState(): void {
    const anyDirty = this.form.controls.some((fg) => fg.dirty)

    if (anyDirty !== this.isEditing) {
      this.isEditing = anyDirty
      this.editing.emit(this.isEditing)
    }
  }

  private getEolBreakdownLocked(r: FprPlasticRow): boolean {
    return !!(r as unknown as { eolBreakdownLocked?: boolean })
      .eolBreakdownLocked
  }

  private normalizeMethodForRow(r: FprPlasticRow): void {
    const allowed = this.getMethodOptions(r)
    if (!allowed.includes(r.methodUsed as FprMethodUsed)) {
      r.methodUsed = allowed[0]
    }
  }

  private getRowKeyFromForm(fg: FormGroup): string {
    return (fg.get('id')?.value as string) ?? ''
  }

  // Compute and emit minimal patches compared to our last emitted snapshot.
  private emitPatches(): void {
    const patches: Array<{ id: string; fields: Partial<FprPlasticRow> }> = []

    for (let i = 0; i < this.form.length; i++) {
      const fg = this.form.at(i)
      const id = this.getRowKeyFromForm(fg)
      if (!id) continue

      // Base payload from the form controls (numeric fields + methodUsed)
      const payload = this.editableSubsetFromForm(fg)

      const prev = this.lastEmittedSnapshot.get(id)
      const fields = this.diffEditable(prev, payload)
      if (Object.keys(fields).length > 0) {
        patches.push({ id, fields })
      }
    }

    if (patches.length > 0) {
      this.patches.emit(patches)
      // Update snapshot baseline to include the newly emitted changes
      for (const p of patches) {
        const prev = this.lastEmittedSnapshot.get(p.id)
        const merged = prev
          ? { ...JSON.parse(prev), ...p.fields }
          : { ...p.fields }
        this.lastEmittedSnapshot.set(p.id, JSON.stringify(merged))
      }
    }
  }

  // Keep only the editable fields so diffs stay tiny.
  private editableSubsetFromRow(r: FprPlasticRow): Partial<FprPlasticRow> {
    const base: Partial<FprPlasticRow> = {
      methodUsed: r.methodUsed,
      qtyManufactured: r.qtyManufactured,
      qtyImported: r.qtyImported,
      qtyPlacedOnMarket: r.qtyPlacedOnMarket,
      qtyAlberta: r.qtyAlberta,
      qtyBritishColumbia: r.qtyBritishColumbia,
      qtyManitoba: r.qtyManitoba,
      qtyNewBrunswick: r.qtyNewBrunswick,
      qtyNewfoundlandAndLabrador: r.qtyNewfoundlandAndLabrador,
      qtyNovaScotia: r.qtyNovaScotia,
      qtyOntario: r.qtyOntario,
      qtyPrinceEdwardIsland: r.qtyPrinceEdwardIsland,
      qtyQuebec: r.qtyQuebec,
      qtySaskatchewan: r.qtySaskatchewan,
      qtyNorthwestTerritories: r.qtyNorthwestTerritories,
      qtyNunavut: r.qtyNunavut,
      qtyYukon: r.qtyYukon,
      qtyWasteGeneratedAtPremises: r.qtyWasteGeneratedAtPremises,
      qtyCollectedAtEndOfLife: r.qtyCollectedAtEndOfLife,
      qtyDivertedDirectReuse: r.qtyDivertedDirectReuse,
      qtyDivertedRefurbishment: r.qtyDivertedRefurbishment,
      qtyDivertedRepair: r.qtyDivertedRepair,
      qtyDivertedRemanufacturing: r.qtyDivertedRemanufacturing,
      qtyDivertedMechanicalRecycling: r.qtyDivertedMechanicalRecycling,
      qtyDivertedChemicalRecycling: r.qtyDivertedChemicalRecycling,
      qtyDivertedProcessingIntoChemicalsFuels:
        r.qtyDivertedProcessingIntoChemicalsFuels,
      qtyDivertedComposting: r.qtyDivertedComposting,
      qtyDivertedIncinerationWithEnergyRecovery:
        r.qtyDivertedIncinerationWithEnergyRecovery,
      qtyDivertedIncinerationIndustrialProcesses:
        r.qtyDivertedIncinerationIndustrialProcesses,
      qtyFinalDisposalIncinerationNoEnergy:
        r.qtyFinalDisposalIncinerationNoEnergy,
      qtyFinalDisposalLandfill: r.qtyFinalDisposalLandfill,
    }

    return base
  }

  private toNumber(v: unknown): number {
    if (v === '' || v === null || v === undefined) return 0
    const n = Number(v)
    return Number.isFinite(n) ? n : 0
  }

  // Snapshot the numeric editable fields from the form so patches stay small.
  private editableSubsetFromForm(fg: FormGroup): Partial<FprPlasticRow> {
    const n = (k: string) => this.toNumber(fg.get(k)?.value)
    return {
      methodUsed: fg.get('methodUsed')?.value,
      qtyManufactured: n('qtyManufactured'),
      qtyImported: n('qtyImported'),
      qtyPlacedOnMarket: n('qtyPlacedOnMarket'),
      qtyAlberta: n('qtyAlberta'),
      qtyBritishColumbia: n('qtyBritishColumbia'),
      qtyManitoba: n('qtyManitoba'),
      qtyNewBrunswick: n('qtyNewBrunswick'),
      qtyNewfoundlandAndLabrador: n('qtyNewfoundlandAndLabrador'),
      qtyNovaScotia: n('qtyNovaScotia'),
      qtyOntario: n('qtyOntario'),
      qtyPrinceEdwardIsland: n('qtyPrinceEdwardIsland'),
      qtyQuebec: n('qtyQuebec'),
      qtySaskatchewan: n('qtySaskatchewan'),
      qtyNorthwestTerritories: n('qtyNorthwestTerritories'),
      qtyNunavut: n('qtyNunavut'),
      qtyYukon: n('qtyYukon'),
      qtyWasteGeneratedAtPremises: n('qtyWasteGeneratedAtPremises'),
      qtyCollectedAtEndOfLife: n('qtyCollectedAtEndOfLife'),
      qtyDivertedDirectReuse: n('qtyDivertedDirectReuse'),
      qtyDivertedRefurbishment: n('qtyDivertedRefurbishment'),
      qtyDivertedRepair: n('qtyDivertedRepair'),
      qtyDivertedRemanufacturing: n('qtyDivertedRemanufacturing'),
      qtyDivertedMechanicalRecycling: n('qtyDivertedMechanicalRecycling'),
      qtyDivertedChemicalRecycling: n('qtyDivertedChemicalRecycling'),
      qtyDivertedProcessingIntoChemicalsFuels: n(
        'qtyDivertedProcessingIntoChemicalsFuels',
      ),
      qtyDivertedComposting: n('qtyDivertedComposting'),
      qtyDivertedIncinerationWithEnergyRecovery: n(
        'qtyDivertedIncinerationWithEnergyRecovery',
      ),
      qtyDivertedIncinerationIndustrialProcesses: n(
        'qtyDivertedIncinerationIndustrialProcesses',
      ),
      qtyFinalDisposalIncinerationNoEnergy: n(
        'qtyFinalDisposalIncinerationNoEnergy',
      ),
      qtyFinalDisposalLandfill: n('qtyFinalDisposalLandfill'),
    }
  }

  // Return a shallow diff of changed keys vs the previous JSON snapshot.
  private diffEditable(
    prevSnap: string | undefined,
    curr: Partial<FprPlasticRow>,
  ): Partial<FprPlasticRow> {
    const prev = prevSnap
      ? (JSON.parse(prevSnap) as Record<string, unknown>)
      : {}
    const diff: Record<string, unknown> = {}
    const currRecord = curr as Record<string, unknown>
    for (const k of Object.keys(currRecord)) {
      if (currRecord[k] !== prev[k]) diff[k] = currRecord[k]
    }
    return diff as Partial<FprPlasticRow>
  }

  // Rebuild totals→children caches to drive group borders/highlighting from current table order.
  private recomputeChildCaches(): void {
    this.totalsChildren.clear()
    this.childToTotals.clear()
    this.firstChildIds.clear()
    this.lastChildIds.clear()

    const rows = this.dataSource.data
    if (!rows || rows.length === 0) return

    for (let i = 0; i < rows.length; i++) {
      const r = rows[i]
      if (
        !r.isTotalRow ||
        !Array.isArray(r.childRowIds) ||
        r.childRowIds.length === 0
      )
        continue

      const set = new Set<string>(r.childRowIds)
      this.totalsChildren.set(r.id, set)
      for (const cid of set) this.childToTotals.set(cid, r.id)

      // Figure out the contiguous block of children just above the total row
      const ordered: string[] = []
      for (let j = i - 1; j >= 0; j--) {
        const prev = rows[j]
        if (!set.has(prev.id)) break
        ordered.push(prev.id)
      }
      ordered.reverse()
      if (ordered.length > 0) {
        this.firstChildIds.add(ordered[0])
        this.lastChildIds.add(ordered[ordered.length - 1])
      }
    }
    this.applyTotalsQuantityRulesForAllTotals()
  }

  // Enable/disable all quantity controls for a row (ignores lock flags by design).
  private setQuantitiesEnabled(fg: FormGroup, enabled: boolean): void {
    for (const k of this.QUANTITY_KEYS) {
      const c = fg.get(k as string)
      if (!c) continue
      if (enabled) {
        if (c.disabled) c.enable({ emitEvent: false })
      } else {
        if (!c.disabled) c.disable({ emitEvent: false })
      }
    }
  }

  // Apply quantity locking rules for a single totals row based on its selected methodology.
  private applyTotalsQuantityRulesForTotal(totalsId: string): void {
    const childIds = this.totalsChildren.get(totalsId)
    const totalsFg = this.idToForm.get(totalsId)
    if (!totalsFg || !childIds || childIds.size === 0) return

    const method = totalsFg.get('methodUsed')?.value as FprMethodUsed
    const isSpecific =
      method ===
      (CALCULATION_METHOD_SPECIFIC_COMPONENT_IDENTIFICATION as FprMethodUsed)

    // Lock parent quantities for Specific Component; enable them otherwise.
    // We don't touch the parent's method selector here.
    this.setQuantitiesEnabled(totalsFg, !isSpecific)

    // When parent is Specific Component → unlock all child quantities and lock their methods.
    // Otherwise → lock child quantities.
    for (const cid of childIds) {
      const childFg = this.idToForm.get(cid)
      if (!childFg) continue

      this.setQuantitiesEnabled(childFg, isSpecific)

      // Child methods should stay locked; enforce disabled state.
      const m = childFg.get('methodUsed')
      if (m && !m.disabled) m.disable({ emitEvent: false })
    }
  }

  // Re-apply rules for every totals row found in the current table.
  private applyTotalsQuantityRulesForAllTotals(): void {
    for (const totalsId of this.totalsChildren.keys()) {
      this.applyTotalsQuantityRulesForTotal(totalsId)
    }
  }

  openResinBreakdown(row: FprPlasticRow) {
    if (!row?.plasticResins?.length) return
    this.bottomSheet.open(PlasticResinsSheetComponent, {
      data: {
        productName: row.productName,
        sku: row.sku,
        plasticResins: row.plasticResins,
      },
      panelClass: 'resin-breakdown-sheet',
    })
  }

  rowHasAllZeroQuantities(row: FprPlasticRow): boolean {
    // Only show the "no quantities" label on the totals row for a group.
    // Child rows in a totals group should not show it.
    if (!row.isTotalRow && this.childToTotals.has(row.id)) {
      return false
    }

    const fg = this.idToForm.get(row.id)

    // If we somehow don't have a form yet, fall back to the row snapshot.
    if (!fg) {
      return this.reportService.areAllQuantitiesZero(row)
    }

    // Build a lightweight object with current numeric quantities from the form.
    const quantities: Partial<Record<keyof FprPlasticRow, number>> = {}
    for (const key of this.QUANTITY_KEYS) {
      const ctrl = fg.get(key as string)
      quantities[key] = this.toNumber(ctrl?.value)
    }

    // Ask the shared helper using the live values instead of the stale row.
    return this.reportService.areAllQuantitiesZero({
      ...row,
      ...quantities,
    } as FprPlasticRow)
  }
}
